import { Anchor, Badge, Card, Group, List, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import { semanticColor } from "@how/design-system";
import { ErrorState, LoadingState } from "@how/ui";

interface PlanModule {
  readonly key: string;
  readonly name: string;
  readonly isBase: boolean;
  readonly inPlan: boolean;
  readonly hasOwnRow: boolean;
  readonly available: boolean;
  readonly sellingModel: string | null;
}
interface MyPlan {
  readonly plan: {
    readonly key: "core" | "pro" | "elite";
    readonly name: string;
    readonly description: string;
    readonly themeAccess: "included" | "premium";
    readonly support: string;
  } | null;
  readonly modules: readonly PlanModule[];
}

const THEMES: Record<"included" | "premium", string> = {
  included: "The included themes",
  premium: "The included and premium themes",
};

/** The church's view of a module: what it has and why, never a price (ADR 0055 §7). */
export function moduleLine(m: PlanModule): string | null {
  if (m.isBase) return null;
  if (m.available)
    return m.inPlan && !m.hasOwnRow
      ? "In your plan"
      : m.inPlan
        ? "In your plan"
        : "Added for your church";
  if (m.inPlan) return "In your plan, turned off for your church";
  if (m.sellingModel === "usage") return "Available by usage; ask us to switch it on";
  return "Not in your plan";
}

/**
 * The church's plan (ADR 0055): what it is, what it includes and what the church has beyond it, read-only. Nothing
 * about prices is shown to church staff until billing exists; changing the plan is a conversation with DCX Logic.
 */
export function PlanCard() {
  const supabase = useSupabase();
  const tenant = useTenant();
  const tenantId = tenant.status === "resolved" ? tenant.tenant.tenantId : "";
  const plan = useQuery({
    queryKey: ["my-plan", tenantId],
    enabled: tenantId !== "",
    queryFn: async (): Promise<MyPlan> => {
      const { data, error } = await supabase.rpc("my_plan", { p_tenant_id: tenantId });
      if (error) throw error;
      return data as unknown as MyPlan;
    },
  });
  return (
    <Card>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <Title order={4}>Your plan</Title>
          <Anchor href="https://dcxlogic.com/contact" target="_blank" rel="noreferrer" size="sm">
            Contact DCX Logic to change it
          </Anchor>
        </Group>
        {plan.isPending ? (
          <LoadingState rows={3} />
        ) : plan.isError ? (
          <ErrorState
            message="Could not load your plan."
            onRetry={() => {
              void plan.refetch();
            }}
          />
        ) : plan.data.plan === null ? (
          <Text size="sm" c="dimmed">
            Your church is not on a plan yet. DCX Logic sets it up with you.
          </Text>
        ) : (
          <>
            <Group gap="sm">
              <Badge
                variant="light"
                color={semanticColor(
                  plan.data.plan.key === "elite"
                    ? "achievement"
                    : plan.data.plan.key === "pro"
                      ? "brand"
                      : "neutral",
                )}
                size="lg"
              >
                {plan.data.plan.name}
              </Badge>
              <Text size="sm" c="dimmed">
                {plan.data.plan.description}
              </Text>
            </Group>
            <Text size="sm">
              {THEMES[plan.data.plan.themeAccess]}
              {plan.data.plan.support === "" ? "" : `. ${plan.data.plan.support}.`}
            </Text>
            <List size="sm" spacing={4}>
              {plan.data.modules
                .filter((m) => !m.isBase)
                .map((m) => (
                  <List.Item key={m.key}>
                    <Group gap="xs">
                      <Text
                        size="sm"
                        fw={m.available ? 600 : 400}
                        {...(m.available ? {} : { c: "dimmed" })}
                      >
                        {m.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {moduleLine(m)}
                      </Text>
                    </Group>
                  </List.Item>
                ))}
            </List>
            <Text size="xs" c="dimmed">
              People, the website and the member portal are part of every plan.
            </Text>
          </>
        )}
      </Stack>
    </Card>
  );
}
